import { WORKFLOW_OBJECT_TYPE_LABEL_MAP } from './workflow-labels';

export type WorkflowIntegrationActionType =
  | 'SEND_WEBHOOK'
  | 'NATIVE_INTEGRATION_ACTION'
  | 'CODE_SNIPPET';

export type WorkflowHttpMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'DELETE';

export interface WorkflowWebhookTestRequest {
  actionType: WorkflowIntegrationActionType;
  objectType?: string;
  objectId?: number;
  config: Record<string, any>;
  context?: Record<string, any>;
}

export interface WorkflowWebhookTestResponse {
  actionType?: string;
  status?: string;
  message?: string;
  targetObjectType?: string;
  targetObjectId?: number | null;
  output?: Record<string, any>;
  error?: Record<string, any>;
  warnings?: string[];
}

export interface WorkflowWebhookPreviewRequest {
  enrollmentId?: number;
  actionType: WorkflowIntegrationActionType;
  config: Record<string, any>;
  context?: Record<string, any>;
}

export interface WorkflowWebhookPreviewResponse {
  actionType?: string;
  renderedUrl?: string;
  renderedMethod?: string;
  renderedHeaders?: Record<string, any>;
  renderedQueryParams?: Record<string, any>;
  renderedBody?: any;
  warnings?: string[];
  securityWarnings?: string[];
}

export interface WorkflowWebhookRenderRequest {
  config: Record<string, any>;
  context?: Record<string, any>;
  enrollmentId?: number;
}

export interface WorkflowWebhookRenderResponse {
  renderedConfig?: Record<string, any>;
  warnings?: string[];
  securityWarnings?: string[];
}

export interface WorkflowWebhookEndpointResponse {
  id: number;
  intIdBussines: number;
  idWorkflow: number;
  workflowName?: string | null;
  idWorkflowVersion?: number | null;
  idNode?: number | null;
  nodeName?: string | null;
  matchObjectType?: string | null;
  matchObjectIdPath?: string | null;
  endpointKey: string;
  name?: string | null;
  description?: string | null;
  isActive: boolean;
  hasSecret: boolean;
  securityWarning?: string | null;
  createdBy?: number | null;
  createdByName?: string | null;
  updatedBy?: number | null;
  updatedByName?: string | null;
  datetimeCreated: string;
  datetimeUpdated: string;
  /**
   * Último cuerpo recibido por el endpoint y sus rutas planas (INC_CRM_020 (9) inc54). El CRM no
   * puede saber de antemano qué manda un sistema externo, así que guarda la última llamada como
   * MUESTRA y el constructor ofrece sus rutas en un desplegable.
   */
  lastPayload?: Record<string, unknown> | null;
  lastPayloadAt?: string | null;
  payloadPaths?: string[] | null;
}

export interface WorkflowWebhookEndpointCreateRequest {
  intIdBussines: number;
  idWorkflow: number;
  idWorkflowVersion?: number | null;
  idNode?: number | null;
  endpointKey: string;
  name?: string | null;
  description?: string | null;
  secret?: string | null;
  isActive?: boolean;
}

export interface WorkflowWebhookEndpointUpdateRequest {
  idWorkflow?: number;
  idWorkflowVersion?: number | null;
  idNode?: number | null;
  endpointKey?: string;
  name?: string | null;
  description?: string | null;
  secret?: string | null;
  isActive?: boolean;
}

export interface WorkflowInboundWebhookTestRequest {
  endpointKey: string;
  secret?: string;
  payload: Record<string, any>;
}

export const WEBHOOK_TEST_EXAMPLE = {
  actionType: 'SEND_WEBHOOK' as WorkflowIntegrationActionType,
  objectType: 'CONTACT',
  config: {
    url: 'https://webhook.site/xxxx',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { contactId: '{{object.id}}', email: '{{contact.email}}' },
    timeoutSeconds: 10,
    responseMapping: [{ jsonPath: '$.invoiceId', targetKey: 'invoiceId' }],
    continueOnError: false
  },
  context: { contact: { email: 'demo@test.com' } }
};

export const INBOUND_WEBHOOK_TEST_EXAMPLE = {
  payload: { dealId: 25, paymentStatus: 'PAID', amount: 500 }
};

/**
 * Alias del diccionario único del módulo (`models/workflow-labels.ts`). Antes era una copia con
 * los mismos cinco tipos, que quedaba corta en cuanto el webhook apuntaba a un lead o una tarea.
 */
export const OBJECT_TYPE_LABELS = WORKFLOW_OBJECT_TYPE_LABEL_MAP;

/** Fila simple para los editores clave-valor (Headers, Body, Payload). */
export interface WebhookKeyValueRow {
  key: string;
  value: string;
}

/** Token curado disponible para insertar en URL/Headers/Body según el tipo de objeto elegido. */
export interface WebhookTokenSuggestion {
  token: string;
  label: string;
}

/**
 * Catálogo de tokens 100% frontend (curado a mano) — NO existe un catálogo de tokens en el
 * backend. Los nombres de campo están verificados contra los datos que realmente arma
 * `WorkflowTokenRenderService` (be_crmv2) para cada objectType: solo se listan campos que el
 * backend efectivamente resuelve. SUBSCRIPTION no tiene mapeo propio en el backend todavía —
 * solo `object.id` está garantizado para ese tipo.
 */
export const WEBHOOK_TOKEN_SUGGESTIONS: Record<string, WebhookTokenSuggestion[]> = {
  CONTACT: [
    { token: '{{object.id}}', label: 'ID del contacto' },
    { token: '{{contact.fullname}}', label: 'Nombre completo' },
    { token: '{{contact.firstname}}', label: 'Nombre (solo el primero)' },
    { token: '{{contact.lastname}}', label: 'Apellido' },
    { token: '{{contact.email}}', label: 'Correo' },
    { token: '{{contact.phone}}', label: 'Teléfono' }
  ],
  COMPANY: [
    { token: '{{object.id}}', label: 'ID de la empresa' },
    { token: '{{company.name}}', label: 'Nombre (comercial o razón social)' },
    { token: '{{company.commercialName}}', label: 'Nombre comercial' },
    { token: '{{company.businessName}}', label: 'Razón social' },
    { token: '{{company.phone}}', label: 'Teléfono' },
    { token: '{{company.website}}', label: 'Sitio web' }
  ],
  DEAL: [
    { token: '{{object.id}}', label: 'ID del negocio' },
    { token: '{{deal.name}}', label: 'Nombre del negocio' },
    { token: '{{deal.amount}}', label: 'Monto' },
    { token: '{{deal.companyId}}', label: 'ID de la empresa asociada' }
  ],
  QUOTE: [
    { token: '{{object.id}}', label: 'ID de la cotización' },
    { token: '{{quote.number}}', label: 'Número de cotización' },
    { token: '{{quote.totalAmount}}', label: 'Monto total' },
    { token: '{{contact.fullname}}', label: 'Nombre del contacto principal' },
    { token: '{{contact.email}}', label: 'Correo del contacto principal' }
  ],
  SUBSCRIPTION: [
    { token: '{{object.id}}', label: 'ID de la suscripción' }
  ]
};

/** Frases legibles para los códigos HTTP más comunes en respuestas de webhooks. */
export const HTTP_STATUS_TEXT: Record<number, string> = {
  200: 'OK',
  201: 'Created',
  202: 'Accepted',
  204: 'No Content',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  408: 'Request Timeout',
  409: 'Conflict',
  410: 'Gone',
  422: 'Unprocessable Entity',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout'
};